import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  DialogContainer,
  Dialog,
  Heading,
  Header,
  Content,
  Footer,
  ButtonGroup,
  Button,
  Text,
} from "@react-spectrum/s2";
import { FeedbackCheckResponse } from '../../types';

const DIMENSIONS: { key: 'relevance' | 'completeness' | 'faithfulness' | 'coherence' }[] = [
  { key: 'relevance' },
  { key: 'completeness' },
  { key: 'faithfulness' },
  { key: 'coherence' },
];

function getVerdictColor(verdict: string): string {
  switch (verdict) {
    case 'good':
      return '#22c55e';
    case 'needs_improvement':
      return '#f59e0b';
    case 'poor':
      return '#ef4444';
    default:
      return '#6b7280';
  }
}

function getVerdictLabel(verdict: string, t: TFunction): string {
  switch (verdict) {
    case 'good':
      return t('chat.judge.verdict.good');
    case 'needs_improvement':
      return t('chat.judge.verdict.needsImprovement');
    case 'poor':
      return t('chat.judge.verdict.poor');
    default:
      return verdict;
  }
}

function getScoreBarColor(score: number): string {
  if (score >= 4) return '#22c55e';
  if (score >= 3) return '#f59e0b';
  return '#ef4444';
}

interface JudgeFeedbackDialogProps {
  qualityScore: FeedbackCheckResponse;
  onClose: () => void;
}

export const JudgeFeedbackDialog: React.FC<JudgeFeedbackDialogProps> = ({
  qualityScore,
  onClose,
}) => {
  const { t } = useTranslation();
  const verdictColor = getVerdictColor(qualityScore.verdict);

  return (
    <DialogContainer onDismiss={onClose}>
      <Dialog size="M">
        <Heading>{t('chat.judge.heading')}</Heading>
        <Header>
          <div className="judge-dialog-verdict-row">
            <span
              className="judge-dialog-verdict-badge"
              style={{ background: verdictColor + '18', color: verdictColor, borderColor: verdictColor + '40' }}
            >
              <span className="judge-dialog-verdict-dot" style={{ background: verdictColor }} />
              {getVerdictLabel(qualityScore.verdict, t)}
            </span>
            <span className="judge-dialog-overall-score">
              {t('chat.judge.scoreOutOf5', { score: qualityScore.quality_score.toFixed(1) })}
            </span>
          </div>
        </Header>


        <Content>
          <div className="judge-dialog-body">
            {DIMENSIONS.map((dim) => {
              const dimensionData = qualityScore[dim.key];
              if (!dimensionData) return null;

              const barColor = getScoreBarColor(dimensionData.score);

              return (
                <div key={dim.key} className="judge-dimension">
                  <div className="judge-dimension-top">
                    <div className="judge-dimension-info">
                      <span className="judge-dimension-label">{t(`chat.feedback.dimensions.${dim.key}.label`)}</span>
                      <span className="judge-dimension-desc">{t(`chat.feedback.dimensions.${dim.key}.description`)}</span>
                    </div>
                    <span className="judge-dimension-score" style={{ color: barColor }}>
                      {t('chat.judge.scoreOutOf5', { score: dimensionData.score })}
                    </span>
                  </div>
                  <div className="judge-dimension-bar-track">
                    <div
                      className="judge-dimension-bar-fill"
                      style={{ width: `${(dimensionData.score / 5) * 100}%`, background: barColor }}
                    />
                  </div>
                  {dimensionData.reasoning && (
                    <p className="judge-dimension-reasoning">{dimensionData.reasoning}</p>
                  )}
                </div>
              );
            })}

            {qualityScore.overall_reasoning && (
              <div className="judge-overall-reasoning">
                <span className="judge-overall-reasoning-label">{t('chat.judge.overallAssessment')}</span>
                <p className="judge-overall-reasoning-text">{qualityScore.overall_reasoning}</p>
              </div>
            )}
          </div>
        </Content>

        <Footer>
          <Text
          UNSAFE_className="judge-dialog-latency">
            {t('chat.judge.evaluatedIn', { seconds: (qualityScore.llm_duration_ms / 1000).toFixed(1) })}
          </Text>
        </Footer>
        <ButtonGroup>
          <Button variant="accent" onPress={onClose} autoFocus>
            {t('chat.judge.done')}
          </Button>
        </ButtonGroup>
      </Dialog>
    </DialogContainer>
  );
};

export default JudgeFeedbackDialog;
